import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '../../../lib/query';
import { createRole, deleteRole, getAvailablePermissions, getRole, getRoles, updateRole } from '../../api/roles/roleApi';


export const useGetAvailablePermissions = () => {
    return useQuery({
        queryKey: ['available-permissions'],
        queryFn: async () => {
            const response = await getAvailablePermissions();
            return response;
        },
    });
};

export const useGetRoles = (params?: Partial<Parameters<typeof getRoles>[0]>) => {
    const resolvedParams = {
        page: params?.page ?? 1,
        limit: params?.limit ?? 10,
    };

    return useQuery({
        queryKey: ['roles', resolvedParams],
        queryFn: async () => {
            const response = await getRoles(resolvedParams);
            return response;
        },
    });
};

export const useGetRole = (id?: number) => {
    return useQuery({
        queryKey: ['role', id],
        queryFn: async () => {
            if (typeof id !== 'number') throw new Error('Role id is required');
            const response = await getRole(id);
            return response;
        },
        enabled: typeof id === 'number',
    });
};

export const useCreateRole = () => {
    return useMutation({
        mutationKey: ['create-role'],
        mutationFn: async (payload: Parameters<typeof createRole>[0]) => {
            const response = await createRole(payload);
            return response;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['roles'], exact: false });
        },
    });
};

export const useUpdateRole = () => {
    return useMutation({
        mutationKey: ['update-role'],
        mutationFn: async (args: { id: number; payload: Parameters<typeof updateRole>[1] }) => {
            const response = await updateRole(args.id, args.payload);
            return response;
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({ queryKey: ['roles'], exact: false });
            await queryClient.invalidateQueries({ queryKey: ['role', variables.id] });
        },
    });
};

export const useDeleteRole = () => {
    return useMutation({
        mutationKey: ['delete-role'],
        mutationFn: async (id: number) => {
            await deleteRole(id);
        },
        onSuccess: async (_data, id) => {
            await queryClient.invalidateQueries({ queryKey: ['roles'], exact: false });
            await queryClient.invalidateQueries({ queryKey: ['role', id] });
        },
    });
};

